/**
 * viewStoreKeys — view-agnostic readers for the calendar's per-view store keys.
 *
 * Each calendar entrypoint registers the SHARED bootstrap/filters slices under
 * its own key (`calendarMonthBootstrap` / `calendarWeekBootstrap` /
 * `calendarDayBootstrap`, and `calendarMonth` / `calendarWeek` / `calendarDay`).
 * Shared components (EventFormBody, EventViewModal, permissions helpers…)
 * historically hardcoded the MONTH keys — silently empty on week/day pages,
 * which disabled the maintenance switch (`canManageMaintenance`), degraded
 * org-settings-driven form behavior, and dropped modal time labels to UTC.
 *
 * These pickers try each view's key in order; exactly one exists per page.
 * TODO(promote-lift): collapse the three registrations into ONE shared key and
 * delete this module.
 *
 * @module viewStoreKeys
 */

/** The active view's bootstrap slice (org, settings, accessGroups, timezone…). */
export const pickCalendarBootstrap = state =>
	state?.calendarMonthBootstrap ||
	state?.calendarWeekBootstrap ||
	state?.calendarDayBootstrap ||
	{};

/** The active view's calendar slice `filters` (timezone etc.), or null. */
export const pickCalendarFilters = state =>
	state?.calendarMonth?.filters ||
	state?.calendarWeek?.filters ||
	state?.calendarDay?.filters ||
	null;

/**
 * The active view's refs slice (activityTypes, customFields, mechanics…).
 *
 * Each entrypoint registers `refsSlice` under its own key (`calendarMonthRefs`
 * / `calendarWeekRefs` / `calendarDayRefs`). Shared modals historically read
 * `calendarMonthRefs` directly → undefined on Week/Day, which silently killed
 * the Activity Type field, org custom fields (+required validation) and the
 * mechanics picker there. Returns `{}` (never undefined) so callers can `?.`
 * a named collection off it without a guard.
 */
export const pickCalendarRefs = state =>
	state?.calendarMonthRefs ||
	state?.calendarWeekRefs ||
	state?.calendarDayRefs ||
	{};

/** The active view's events map (`{ [id]: event }`), or `{}`. */
export const pickCalendarEvents = state =>
	state?.calendarMonth?.events ||
	state?.calendarWeek?.events ||
	state?.calendarDay?.events ||
	{};
